import momentTimezone from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';

import { FetchSaleQuery, FetchSaleQueryVariables } from 'src/apollo/types/FetchSaleQuery';
import { DeliveryModifier, PickedBox, PickingEventTypes } from 'src/apollo/types/globalTypes';
import { LogEventMutation, LogEventMutationVariables } from 'src/apollo/types/LogEventMutation';
import {
  PrintRetailBoxLabelsQueueMutation,
  PrintRetailBoxLabelsQueueMutationVariables,
} from 'src/apollo/types/PrintRetailBoxLabelsQueueMutation';
import {
  PrintRetailBoxLabelsSseMutation,
  PrintRetailBoxLabelsSseMutationVariables,
} from 'src/apollo/types/PrintRetailBoxLabelsSseMutation';
import { RetailBoxLabelsQuery, RetailBoxLabelsQueryVariables } from 'src/apollo/types/RetailBoxLabelsQuery';
import {
  AUTH_TOKEN,
  DISTRIBUTION_CENTER,
  EMAIL,
  IS_PRINTER_COMPUTER,
  LOGISTIC_KIND,
  PC_ID,
  PRODUCT_TYPE,
} from 'src/common/constants';
import { errorFormat } from 'src/common/errorFormatting';
import { SaleInfo } from 'src/common/saleInfo/organisms/SaleInfo';
import { PickingStatusContainer } from 'src/common/status';
import { FormattedSale, LogisticKinds, LogisticKindsPT, PickingTypes, PickingTypesPT } from 'src/common/types';
import { playErrorSound } from 'src/common/picking/logic/sounds';
import { StartPickingMutation, StartPickingMutationVariables } from '../../apollo/types/StartPickingMutation';
import { PageTitle } from '../../components/molecules/pageTitle';
import {
  CANCEL_PICKING_MUTATION,
  FETCH_SALE,
  FINISH_PICKING_MUTATION,
  GET_TAG_QUERY,
  LOG_EVENT_MUTATION,
  PRINT_RETAIL_BOX_LABELS_QUEUE_MUTATION,
  PRINT_RETAIL_BOX_LABELS_SSE_MUTATION,
  PRINT_TAG_SSE_MUTATION,
  PRINT_TAGS_MUTATION,
  RETAIL_BOX_LABELS_QUERY,
  START_PICKING_MUTATION,
} from './graphql';
import { BOX_PICKING_MODE, BoxPickingItem, clearStoredBoxes, getBoxPickingStorageKey } from './logic/boxPicking';
import {
  getMultiLabelPackages,
  RetailBoxLabelsBlock,
  RetailLabelsPrinter,
  RetailLabelsRequest,
} from './logic/retailLabels';
import { formatProductsBySku } from './logic/formatting/formatProductsBySku';
import { formatSaleForPicking } from './logic/formatting/formatSaleForPicking';
import { getTotalBags } from './logic/getTotalBags';
import { pcIdValidationError } from './logic/pcIdIsValid';
import { SseHttpError, usePrintingSSE } from './hooks/usePrintingSSE';
import { DEFAULT_PRINT_STRATEGY, getDefaultZebraDevice, printTags, PrintStrategy, printZpl } from './logic/printing';
import { BoxPicking } from './organisms/boxPicking';
import { RetailBoxLabels } from './organisms/retailBoxLabels';
import { ExpressOrderIndicator } from './organisms/expressOrderIndicator';
import { UrgentOrderIndicator } from './organisms/urgentOrderIndicator';
import { OrdersPerPeriodContainer } from './organisms/ordersPerPeriodContainer';
import { PickingFinished } from './organisms/pickingFinished';
import { PickingInitialForm } from './organisms/pickingInitialForm';
import PrinterStationConfig from './organisms/printerStationConfig';
import { StepperContainer } from './organisms/stepper';
import {
  InitialFormAndStatusContainer,
  InitialFormContainer,
  OrdersPerPeriodAndSaleInfoContainer,
  StatusContainer,
  StepperAndInfosContainer,
  StyledContainer,
} from './styles';

export interface LoginProps {
  refreshToken?: (data: any) => void;
  printStrategy?: PrintStrategy;
}

const SSE_CONNECTING_KEY = 'sse-connecting';

export interface FinishPickingParams {
  packageCodes?: string[];
  numberOfPackages?: string;
  pickingProblems?: any;
  productBatches?: string[];
  boxes?: PickedBox[];
}

const Picking: React.FC<LoginProps> = ({ ...props }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [distributionCenter, setDistributionCenter] = useState(
    localStorage.getItem(DISTRIBUTION_CENTER) && JSON.parse(localStorage.getItem(DISTRIBUTION_CENTER))
  );
  const [productType, setProductType] = useState(localStorage.getItem(PRODUCT_TYPE));
  const [logisticKind, setLogisticKind] = useState(localStorage.getItem(LOGISTIC_KIND) as LogisticKinds);
  const [pcId, setPcId] = useState((localStorage.getItem(PC_ID) && Number(localStorage.getItem(PC_ID))) || 0);
  const [isPrinterComputer, setIsPrinterComputer] = useState(localStorage.getItem(IS_PRINTER_COMPUTER) === 'true');
  const [order, setOrder] = useState('');
  const [isExpressDelivery, setIsExpressDelivery] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [productsBySku, setProductsBySku] = useState();
  const [packageCodes, setPackageCodes] = useState([]);
  const [numberOfPackages, setNumberOfPackages] = useState('');
  const [pickingProblems, setPickingProblems] = useState();
  const [packageSize, setPackageSize] = useState<string>();
  const [alreadyFinishedPicking, setAlreadyFinishedPicking] = useState(false);
  const [rejectedBatches, setRejectedBatches] = useState([]);
  const [firstOrder, setFirstOrder] = useState(false);
  const [hasBougtGreenGrocer, setHasBougtGreenGrocer] = useState(false);
  const [_secondOrder, setSecondOrder] = useState(false);
  const [observations, setObservations] = useState('');
  const [insideObservations, setInsideObservations] = useState('');
  const [period, setPeriod] = useState('');
  const [dateString, setDateString] = useState('');
  const [formattedSale, setFormattedSale] = useState<FormattedSale>();
  const [user, setUser] = useState('');
  const [step, setStep] = useState(0);
  const [finishSuccess, setFinishSuccess] = useState(false);
  const [totalTags, setTotalTags] = useState(0);
  const [fetchSale, setFetchSale] = useState(false);
  const [finishPickingParams, setFinishPickingParams] = useState<FinishPickingParams>({});
  const [hasBabyFood, setHasBabyFood] = useState(false);
  const [promotionalItems, setPromotionalItems] = useState([]);
  const [pickingMode, setPickingMode] = useState<string | null>(null);
  const [boxPickingItems, setBoxPickingItems] = useState<BoxPickingItem[]>([]);
  const [minShelfLifePercent, setMinShelfLifePercent] = useState<number | null>(null);
  const [finishedPickingMode, setFinishedPickingMode] = useState<string | null>(null);
  const isBoxPicking = pickingMode === BOX_PICKING_MODE;
  const zebraDeviceRef = useRef<ZebraDevice | null>(null);
  const hasEverConnectedRef = useRef(false);
  const isZplStrategy = props.printStrategy === 'zpl';
  const [isBusy, setIsBusy] = useState(false);
  const [isPrinterAvailable, setIsPrinterAvailable] = useState(false);

  useEffect(() => {
    if (!isPrinterComputer) {
      closeSnackbar(SSE_CONNECTING_KEY);
      hasEverConnectedRef.current = false;
    }
  }, [isPrinterComputer, closeSnackbar]);

  useEffect(() => {
    if (!isZplStrategy) {
      return;
    }
    getDefaultZebraDevice()
      .then(device => {
        zebraDeviceRef.current = device;
        setIsPrinterAvailable(true);
      })
      .catch(() => {
        setIsPrinterAvailable(false);
      });
  }, [isZplStrategy, isPrinterComputer]);

  const handleError = (error: Error) => {
    playErrorSound();
    enqueueSnackbar(errorFormat(error), {
      variant: 'error',
    });
  };

  const handlePrintZpl = useCallback(async (zpl: string): Promise<void> => {
    if (!zebraDeviceRef.current) {
      throw new Error('Impressora não conectada');
    }
    return printZpl(zebraDeviceRef.current, zpl);
  }, []);

  const apolloClient = useApolloClient();
  const shouldPrintLocally = isZplStrategy && isPrinterAvailable && !isPrinterComputer;
  const printLocally = useCallback(
    async (orderArg: string, tagCode: string): Promise<void> => {
      try {
        const result = await apolloClient.query({
          query: GET_TAG_QUERY,
          variables: { input: { order: orderArg, tagCode } },
          fetchPolicy: 'no-cache',
        });
        await handlePrintZpl(result.data.getTag);
      } catch (error) {
        handleError(error);
      }
    },
    [apolloClient, handlePrintZpl]
  );

  const findZebraDevice = useCallback(async (): Promise<ZebraDevice | null> => {
    if (!zebraDeviceRef.current) {
      zebraDeviceRef.current = await getDefaultZebraDevice().catch(() => null);
    }
    return zebraDeviceRef.current;
  }, []);

  const fetchRetailBoxLabels = useCallback(
    async ({ order: labelsOrder, from, to, countOnly }: RetailLabelsRequest): Promise<RetailBoxLabelsBlock> => {
      const result = await apolloClient.query<RetailBoxLabelsQuery, RetailBoxLabelsQueryVariables>({
        query: RETAIL_BOX_LABELS_QUERY,
        variables: { input: { order: labelsOrder, productType, from, to, countOnly } },
        fetchPolicy: 'no-cache',
      });
      return result.data.retailBoxLabels;
    },
    [apolloClient, productType]
  );

  const sendRetailBoxLabels = useCallback(
    async (labelsOrder: string, from: number, to: number): Promise<RetailBoxLabelsBlock> => {
      const result = await apolloClient.mutate<
        PrintRetailBoxLabelsSseMutation,
        PrintRetailBoxLabelsSseMutationVariables
      >({
        mutation: PRINT_RETAIL_BOX_LABELS_SSE_MUTATION,
        variables: {
          input: { order: labelsOrder, productType, from, to, pcId, distributionCenterId: distributionCenter.value },
        },
      });
      if (!result.data) {
        throw new Error('O Dobby não confirmou o envio das etiquetas. Tente de novo.');
      }
      return { ...result.data.printRetailBoxLabelsSSE, zpl: '' };
    },
    [apolloClient, productType, pcId, distributionCenter]
  );

  const resolveRetailLabelsPrinter = useCallback(
    async (labelsOrder: string): Promise<RetailLabelsPrinter> => {
      if (await findZebraDevice()) {
        return {
          kind: 'local',
          fetchLabels: (from, to) => fetchRetailBoxLabels({ order: labelsOrder, from, to }),
          printZpl: handlePrintZpl,
        };
      }
      return {
        kind: 'printerComputer',
        pcId,
        sendLabels: (from, to) => sendRetailBoxLabels(labelsOrder, from, to),
      };
    },
    [findZebraDevice, fetchRetailBoxLabels, handlePrintZpl, pcId, sendRetailBoxLabels]
  );

  const sendRetailBoxLabelsToQueue = useCallback(
    async (labelsOrder: string, from: number, to: number): Promise<RetailBoxLabelsBlock> => {
      const result = await apolloClient.mutate<
        PrintRetailBoxLabelsQueueMutation,
        PrintRetailBoxLabelsQueueMutationVariables
      >({
        mutation: PRINT_RETAIL_BOX_LABELS_QUEUE_MUTATION,
        variables: { input: { order: labelsOrder, productType, from, to, pcId } },
      });
      if (!result.data) {
        throw new Error('O Dobby não confirmou o envio das etiquetas. Confira se elas saíram antes de mandar de novo.');
      }
      return { ...result.data.printRetailBoxLabelsQueue, zpl: '' };
    },
    [apolloClient, productType, pcId]
  );

  const resolveQueuePrinter = useCallback(
    async (labelsOrder: string): Promise<RetailLabelsPrinter> => ({
      kind: 'printQueue',
      pcId,
      sendLabels: (from, to) => sendRetailBoxLabelsToQueue(labelsOrder, from, to),
    }),
    [pcId, sendRetailBoxLabelsToQueue]
  );

  const resolveStationPrinter = isZplStrategy ? resolveRetailLabelsPrinter : resolveQueuePrinter;

  usePrintingSSE({
    isEnabled: isZplStrategy && isPrinterComputer && isPrinterAvailable,
    distributionCenterId: distributionCenter && distributionCenter.value,
    pcId,
    onPrintZpl: handlePrintZpl,
    onError: handleError,
    onConnecting: () => {
      setIsBusy(true);
      const text = hasEverConnectedRef.current ? 'Reconectando na impressora...' : 'Conectando na impressora...';
      enqueueSnackbar(text, {
        variant: 'info',
        persist: true,
        key: SSE_CONNECTING_KEY,
        preventDuplicate: true,
      });
    },
    onConnected: () => {
      setIsBusy(false);
      closeSnackbar(SSE_CONNECTING_KEY);
      const text = hasEverConnectedRef.current
        ? 'Conexão com a impressora restabelecida.'
        : 'Pronto para receber impressões.';
      enqueueSnackbar(text, { variant: 'success', preventDuplicate: true });
      hasEverConnectedRef.current = true;
    },
    onDisconnected: () => {
      enqueueSnackbar('Conexão com a impressora perdida. Tentando reconectar...', {
        variant: 'error',
        preventDuplicate: true,
      });
    },
    onConnectionError: (err: Error) => {
      setIsBusy(false);
      if (err instanceof SseHttpError && err.status === 401) {
        closeSnackbar(SSE_CONNECTING_KEY);
        enqueueSnackbar('Sessão expirada. Faça login novamente para retomar as impressões.', {
          variant: 'error',
          preventDuplicate: true,
        });
      } else if (err instanceof SseHttpError && err.status === 409) {
        closeSnackbar(SSE_CONNECTING_KEY);
        localStorage.setItem(IS_PRINTER_COMPUTER, 'false');
        setIsPrinterComputer(false);
        enqueueSnackbar(
          `Outra sessão de impressão já está ativa para o computador nº ${pcId}. Marque "Receber pedidos de impressão de outros computadores" novamente para tentar de novo.`,
          {
            variant: 'warning',
            preventDuplicate: true,
            autoHideDuration: 8000,
          }
        );
      }
    },
    onPrinting: tagCode =>
      enqueueSnackbar(`Imprimindo etiqueta "${tagCode}"...`, { variant: 'info', preventDuplicate: true }),
    onAbort: () => {
      setIsBusy(false);
      closeSnackbar(SSE_CONNECTING_KEY);
    },
  });

  const handleNextPickingError = (error: Error) => {
    if (errorFormat(error) === 'Pedido já separado.') {
      setFetchSale(true);
      setAlreadyFinishedPicking(true);
    } else if (errorFormat(error) === 'Pedido de varejo já separado.') {
      setStep(3);
    } else {
      setOrder('');
      setStep(0);
      setFinishSuccess(false);
      handleError(error);
    }
  };

  const [startPickingMutation, { loading: startingPicking }] = useMutation<
    StartPickingMutation,
    StartPickingMutationVariables
  >(START_PICKING_MUTATION, {
    variables: {
      input: {
        distributionCenter: distributionCenter && distributionCenter.value,
        productType,
        logisticKind,
      },
    },
    onError: handleNextPickingError,
  });

  const [logEventMutation] = useMutation<LogEventMutation, LogEventMutationVariables>(LOG_EVENT_MUTATION);

  const handleSaleFetched = (data: FetchSaleQuery) => {
    if (data && data.sale) {
      try {
        const newProductsBySku = formatProductsBySku(data.sale.products);
        setProductsBySku(newProductsBySku);
        setHasBabyFood(!!data.sale.cart.items.find(item => item.item.type === 'babyFood'));
        setPeriod(data.sale.cart.deliverySettings.chosenPeriod.id);
        setDateString(data.sale.cart.deliverySettings.chosenDateString);
        setIsExpressDelivery(data.sale.cart.deliverySettings.deliveryModifier === DeliveryModifier.EXPRESS);
        setIsUrgent(data.sale.isUrgent || false);
        setUser(data.sale.user.name || '');
        setFinishSuccess(false);
        setFetchSale(false);
        setObservations(data.sale.observations || '');
        setInsideObservations((data.sale.insideObservations && data.sale.insideObservations.text) || '');

        if (!isBoxPicking) {
          const newFormattedSale = formatSaleForPicking(
            data.sale,
            newProductsBySku,
            productType as PickingTypes,
            firstOrder,
            packageSize
          );
          setFormattedSale(newFormattedSale);
          const quantity = !alreadyFinishedPicking ? getTotalBags(newFormattedSale.packageSizes) : 0;
          setTotalTags(quantity);

          if (!alreadyFinishedPicking) {
            printTags({
              pcId: String(pcId),
              order,
              quantity,
              productType,
              printStrategy: props.printStrategy || DEFAULT_PRINT_STRATEGY,
              printMutation: printTagsMutation,
              printTagSSEMutation: printTagSSEMutation,
              distributionCenterId: distributionCenter ? distributionCenter.value : null,
              printLocally: shouldPrintLocally ? printLocally : undefined,
            });
          }
        }
        setStep(1);
      } catch (err) {
        setFinishSuccess(false);
        setFetchSale(false);
        enqueueSnackbar(err.message, {
          variant: 'error',
        });
        handleCancel();
      }
    }
  };

  const handleFinished = () => {
    if (isBoxPicking && distributionCenter) {
      clearStoredBoxes(getBoxPickingStorageKey(distributionCenter.value, order));
    }
    setFinishedPickingMode(pickingMode);
    setStep(2);
    setFinishSuccess(true);
  };

  const handleFetchSaleError = (error: Error) => {
    handleCancel();
    handleError(error);
  };
  const handleFinishPickingError = (error: Error) => {
    if (isBoxPicking && errorFormat(error) === 'Pedido de varejo já separado.') {
      enqueueSnackbar('O pedido já consta como finalizado. Buscando as etiquetas de expedição.', {
        variant: 'info',
      });
      handleFinished();
      return;
    }
    if (!isBoxPicking) {
      setStep(2);
      setFinishSuccess(false);
    }
    handleError(error);
  };

  const { loading: fetchingSale } = useQuery<FetchSaleQuery, FetchSaleQueryVariables>(FETCH_SALE, {
    skip: !fetchSale,
    fetchPolicy: 'network-only',
    onCompleted: handleSaleFetched,
    variables: { order: Number(order) },
    onError: handleFetchSaleError,
  });

  const [printTagsMutation] = useMutation(PRINT_TAGS_MUTATION, {
    onError: handleError,
  });

  const [printTagSSEMutation] = useMutation(PRINT_TAG_SSE_MUTATION, {
    onError: handleError,
  });

  const [cancelPickingMutation] = useMutation(CANCEL_PICKING_MUTATION, {
    variables: { input: { order, productType } },
  });

  const [finishPickingMutation, { loading: finishingPicking }] = useMutation(FINISH_PICKING_MUTATION, {
    onCompleted: handleFinished,
    onError: handleFinishPickingError,
  });

  const nextPicking = async ({ pickingLine = false }) => {
    const pcIdError = pcIdValidationError(pcId);
    if (!distributionCenter) {
      enqueueSnackbar('Selecione o centro de distribuição', {
        variant: 'error',
      });
      return;
    }
    if (!productType) {
      enqueueSnackbar('Informe o tipo de produto', {
        variant: 'error',
      });
      return;
    }
    if (!logisticKind) {
      enqueueSnackbar('Informe o tipo do cliente', {
        variant: 'error',
      });
      return;
    }
    if (pcIdError) {
      enqueueSnackbar(pcIdError, {
        variant: 'error',
        autoHideDuration: 8000,
      });
      return;
    }

    setPickingMode(null);
    setBoxPickingItems([]);
    setMinShelfLifePercent(null);

    const result = await startPickingMutation({
      variables: {
        input: {
          distributionCenter: distributionCenter.value,
          productType,
          logisticKind,
          ...(pcId && pcId !== 0 && { pcId }),
          ...(!pickingLine && order && order !== '' && { order }),
        },
      },
    });
    const startPicking = result && result.data && result.data.startPicking;

    if (startPicking && startPicking.order) {
      logEventMutation({
        variables: {
          input: {
            event: PickingEventTypes.BAG_PICKING,
            order: startPicking.order,
            user: localStorage.getItem(EMAIL),
            payload: order && order !== '' ? 'manual' : 'picking line',
            timestamp: momentTimezone()
              .tz('America/Sao_Paulo')
              .format(),
          },
        },
      });
      setRejectedBatches(startPicking.rejectedBatches);
      setFirstOrder(startPicking.first);
      setSecondOrder(startPicking.second);
      setHasBougtGreenGrocer(startPicking.hasBoughtGreenGrocer);
      setOrder(startPicking.order);
      setPackageSize(startPicking.packageSize);
      setPromotionalItems(startPicking.promotionalItems);
      setPickingMode(startPicking.pickingMode || null);
      setBoxPickingItems(startPicking.boxPickingItems || []);
      setMinShelfLifePercent(startPicking.minShelfLifePercent);
      setFetchSale(true);
    }
  };

  const handleOrderInputChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setOrder(value);
  };

  const handlePcIdInputChange = (event: any) => {
    const {
      target: { value },
    } = event;
    localStorage.setItem(PC_ID, String(Number(value)));
    setPcId(Number(value));
  };

  const handlePcIdChange = (id: number) => {
    localStorage.setItem(PC_ID, String(id));
    setPcId(id);
  };

  const handleTogglePrinterComputer = (value: boolean) => {
    const pcIdError = value ? pcIdValidationError(pcId) : null;
    if (pcIdError) {
      enqueueSnackbar(pcIdError, {
        variant: 'error',
        autoHideDuration: 8000,
      });
      return;
    }
    localStorage.setItem(IS_PRINTER_COMPUTER, String(value));
    setIsPrinterComputer(value);
  };

  const handleDistributionCenterSelect = (option: any) => {
    localStorage.setItem(DISTRIBUTION_CENTER, JSON.stringify(option));
    setDistributionCenter(option);
  };

  const handleProductTypeSelect = (index: string) => {
    localStorage.setItem(PRODUCT_TYPE, Object.keys(PickingTypesPT)[Number(index)]);
    setProductType(Object.keys(PickingTypesPT)[Number(index)]);
  };

  const handleLogisticKindSelect = (index: string) => {
    const value = Object.keys(LogisticKinds)[Number(index)] as LogisticKinds;
    localStorage.setItem(LOGISTIC_KIND, value);
    setLogisticKind(value);
  };

  const handleFinishPicking = async (params: FinishPickingParams) => {
    setFinishPickingParams(params);
    setPackageCodes(params.packageCodes);
    setNumberOfPackages(params.numberOfPackages);
    setPickingProblems(params.pickingProblems);
    try {
      await finishPickingMutation({
        variables: {
          input: {
            order,
            productBatches: params.productBatches,
            distributionCenter: distributionCenter.value,
            productType,
            packageCodes: params.packageCodes || packageCodes,
            numberOfPackages: params.numberOfPackages || numberOfPackages || null,
            pickingProblems: params.pickingProblems || pickingProblems,
            boxes: params.boxes,
          },
        },
      });
    } catch (e) {}
  };

  const handleCancel = () => {
    if (order) {
      cancelPickingMutation({
        variables: {
          input: {
            order,
            productType,
          },
        },
      });
      setOrder('');
    }
    setStep(0);
  };

  const handleForceCancel = () => {
    if (order) {
      const cancelBody = {
        query: `
    mutation CancelPickingMutation($input: CancelPicking!) {
      cancelPicking(input: $input)
    }
  `,
        operationName: 'CancelPickingMutation',
        variables: { input: { order, productType } },
      };

      const authToken = localStorage.getItem(AUTH_TOKEN);

      const client = new XMLHttpRequest();
      client.open('POST', `${process.env.DOBBY_API}/graphql`, false); // third parameter indicates sync xhr
      client.setRequestHeader('content-type', 'application/json');
      client.setRequestHeader('authorization', `Bearer ${authToken}`);
      client.send(JSON.stringify(cancelBody));
    }
  };

  const handleBack = () => {
    setOrder('');
    setStep(0);
    setAlreadyFinishedPicking(false);
    setFinishedPickingMode(null);
  };

  const renderPageTitle = () =>
    step === 0 ? <PageTitle>Iniciar Picking</PageTitle> : <PageTitle>Estação de Picking</PageTitle>;

  const renderPickingInitialForm = () =>
    step === 0 && (
      <InitialFormAndStatusContainer>
        <InitialFormContainer>
          <PickingInitialForm
            selectedDistributionCenter={distributionCenter}
            onDistributionCenterSelect={handleDistributionCenterSelect}
            onProductTypeSelect={handleProductTypeSelect}
            onLogisticKindSelect={handleLogisticKindSelect}
            productTypes={Object.values(PickingTypesPT)}
            logisticKindsValues={Object.values(LogisticKinds)}
            logisticKindsLabels={Object.values(LogisticKindsPT)}
            logisticKind={logisticKind}
            pickingType={PickingTypesPT[productType]}
            onOrderInputChange={handleOrderInputChange}
            onPcIdInputChange={handlePcIdInputChange}
            nextPicking={nextPicking}
            loadingSale={startingPicking || fetchingSale || isBusy}
            onError={handleError}
            pcId={pcId}
            printStrategy={props.printStrategy}
          />
        </InitialFormContainer>
        <StatusContainer>
          <PickingStatusContainer
            distributionCenter={distributionCenter && distributionCenter.value}
            size="large"
            productType={productType}
            logisticKind={logisticKind}
            onError={handleError}
          />
          {isZplStrategy && (
            <PrinterStationConfig
              isPrinterComputer={isPrinterComputer}
              pcId={pcId}
              onTogglePrinterComputer={handleTogglePrinterComputer}
              onPcIdChange={handlePcIdChange}
              isPrinterDeviceAvailable={isPrinterAvailable}
            />
          )}
        </StatusContainer>
      </InitialFormAndStatusContainer>
    );

  const renderBoxPicking = () =>
    step === 1 &&
    isBoxPicking && (
      <BoxPicking
        order={order}
        distributionCenter={distributionCenter.value}
        items={boxPickingItems}
        productsBySku={productsBySku}
        rejectedBatches={rejectedBatches}
        minShelfLifePercent={minShelfLifePercent}
        finishingPicking={finishingPicking}
        isExpressDelivery={isExpressDelivery}
        isUrgent={isUrgent}
        onFinish={handleFinishPicking}
        onCancel={handleCancel}
        onForceCancel={handleForceCancel}
      />
    );

  const renderStepper = () =>
    step === 1 &&
    !isBoxPicking && (
      <StepperContainer
        onBack={handleBack}
        alreadyFinishedPicking={alreadyFinishedPicking}
        order={order}
        isExpressDelivery={isExpressDelivery}
        isUrgent={isUrgent}
        productType={productType}
        productsBySku={productsBySku}
        sale={formattedSale}
        onFinish={handleFinishPicking}
        totalTags={totalTags}
        observations={observations}
        insideObservations={insideObservations}
        onCancel={handleCancel}
        onForceCancel={handleForceCancel}
        finishingPicking={finishingPicking}
        firstOrder={firstOrder}
        hasBabyFood={hasBabyFood}
        secondOrder={false}
        onError={handleError}
        rejectedBatches={rejectedBatches}
        pcId={pcId}
        distributionCenter={distributionCenter.value}
        promotionalItems={promotionalItems}
        hasBoughtGreenGrocer={hasBougtGreenGrocer}
        printStrategy={props.printStrategy}
        printLocally={shouldPrintLocally ? printLocally : undefined}
      />
    );

  const renderPickingFinished = () =>
    step === 2 &&
    (finishedPickingMode === BOX_PICKING_MODE && finishSuccess ? (
      <RetailBoxLabels
        order={order}
        boxCount={finishPickingParams.boxes ? finishPickingParams.boxes.length : null}
        packageCount={
          finishPickingParams.boxes ? finishPickingParams.boxes.filter(box => !box.samePackageAsPrevious).length : null
        }
        multiLabelPackages={finishPickingParams.boxes ? getMultiLabelPackages(finishPickingParams.boxes) : null}
        autoPrint
        fetchLabels={fetchRetailBoxLabels}
        resolvePrinter={resolveStationPrinter}
        loading={startingPicking || fetchingSale}
        onNextPicking={() => nextPicking({ pickingLine: true })}
        onBack={handleBack}
      />
    ) : (
      <PickingFinished
        success={finishSuccess}
        nextPicking={nextPicking}
        order={order}
        isExpressDelivery={isExpressDelivery}
        loading={startingPicking || fetchingSale}
        finishingPicking={finishingPicking}
        params={finishPickingParams}
        onRetry={handleFinishPicking}
        onCancel={handleCancel}
        onForceCancel={handleForceCancel}
        onBack={handleBack}
      />
    ));

  const renderRetailReprint = () =>
    step === 3 && (
      <RetailBoxLabels
        order={order}
        boxCount={null}
        packageCount={null}
        multiLabelPackages={null}
        autoPrint={false}
        fetchLabels={fetchRetailBoxLabels}
        resolvePrinter={resolveStationPrinter}
        loading={startingPicking || fetchingSale}
        onBack={handleBack}
      />
    );

  const renderOrdersPerPeriodAndSaleInfoContainer = () =>
    step !== 2 &&
    step !== 3 && (
      <OrdersPerPeriodAndSaleInfoContainer>
        {isExpressDelivery ? renderExpressOrderIndicator() : isUrgent ? renderUrgentOrderIndicator() : renderOrdersPerPeriod()}
        {renderSaleInfo()}
      </OrdersPerPeriodAndSaleInfoContainer>
    );

  const renderOrdersPerPeriod = () =>
    step !== 0 && period && <OrdersPerPeriodContainer period={period} dateString={dateString} />;

  const renderExpressOrderIndicator = () => step !== 0 && period && <ExpressOrderIndicator />;

  const renderUrgentOrderIndicator = () =>
    step !== 0 && period && <UrgentOrderIndicator period={period} dateString={dateString} />;

  const renderSaleInfo = () =>
    step !== 0 && (
      <SaleInfo
        customer={user}
        order={order}
        isExpressDelivery={isExpressDelivery}
        isUrgent={isUrgent}
        distributionCenter={distributionCenter.label}
        observations={insideObservations}
      />
    );

  return (
    <StyledContainer>
      {renderPageTitle()}
      {renderPickingInitialForm()}
      <StepperAndInfosContainer>
        {renderBoxPicking()}
        {renderStepper()}
        {renderPickingFinished()}
        {renderRetailReprint()}
        {renderOrdersPerPeriodAndSaleInfoContainer()}
      </StepperAndInfosContainer>
    </StyledContainer>
  );
};
export default Picking;
